import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import colors from 'modules/App/colors';

export type Area = {
  tooltip: React.ReactNode;
  geo: {
    type: string;
    coordinates: [number, number]; // [lng, lat]
  };
  radius?: number;
  onClick?: () => void;
  icon?: string;
  key: string;
  className?: string;
};

export type MapComponentProps = {
  areas: Area[];
  className?: string;
  style?: React.CSSProperties;
};

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

const MapComponent: React.FC<MapComponentProps> = ({ areas, className, style }) => {
  const mapRef = useRef<L.Map>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // CSS pour les popups hover
    const style = document.createElement('style');
    style.textContent = `
      .hover-popup .leaflet-popup-content-wrapper {
        transition: opacity 0.2s ease-in-out;
        max-height: 400px;
      }
      
      .hover-popup .leaflet-popup {
        max-height: 400px;
      }
      
      .hover-popup .leaflet-popup-content {
        margin: 8px 12px;
        max-height: 380px;
        overflow: visible;
      }
      
      /* Contraindre la position du popup dans la carte */
      .leaflet-container .hover-popup {
        max-height: 400px !important;
      }
      
      /* S'assurer que le contenu interne est scrollable */
      .hover-popup .max-h-96 {
        max-height: 384px !important;
        overflow-y: auto !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Calculate bounds based on all markers
  const bounds = React.useMemo(() => {
    const validAreas = areas.filter((area) => area.geo?.coordinates);
    if (validAreas.length === 0) {
      return L.latLngBounds([
        [48.8566, 2.3522],
        [48.8566, 2.3522],
      ]); // Default to Paris
    }

    return L.latLngBounds(validAreas.map((area) => [area.geo.coordinates[1], area.geo.coordinates[0]]));
  }, [areas]);

  // Fit map to bounds when areas change
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds]);

  return (
    <div className={className} style={style}>
      <MapContainer
        ref={mapRef}
        center={[48.8566, 2.3522]} // Default center, will be adjusted by bounds
        zoom={5}
        style={{ width: '100%', minHeight: '100%', zIndex: 1 }}
        className="[&_.marker-cluster-small]:bg-primary [&_.marker-cluster-medium]:bg-secondary [&_.marker-cluster-large]:bg-accent"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerClusterGroup
          maxClusterRadius={32}
          polygonOptions={{
            fillColor: 'transparent',
            color: colors.primary,
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          }}
        >
          {areas.map((area, idx) =>
            area.geo?.coordinates ? (
              <Marker
                key={area.key + idx}
                position={[area.geo.coordinates[1], area.geo.coordinates[0]]}
                icon={
                  area.icon
                    ? L.icon({
                        iconUrl: area.icon,
                        iconSize: [48, 48],
                        iconAnchor: [48, 48],
                        popupAnchor: [1, -34],
                        tooltipAnchor: [16, -28],
                        className: area.className,
                      })
                    : defaultIcon
                }
                eventHandlers={{
                  click: area.onClick,
                  mouseover: (e) => {
                    // Annuler tout timeout de fermeture en cours
                    if (closeTimeoutRef.current) {
                      clearTimeout(closeTimeoutRef.current);
                      closeTimeoutRef.current = null;
                    }
                    e.target.openPopup();
                  },
                  mouseout: (e) => {
                    // Programmer la fermeture avec un délai
                    closeTimeoutRef.current = setTimeout(() => {
                      const popup = e.target.getPopup();
                      if (popup && popup._container) {
                        const isHoveringPopup = popup._container.matches(':hover');
                        if (!isHoveringPopup) {
                          e.target.closePopup();
                        }
                      }
                      closeTimeoutRef.current = null;
                    }, 300);
                  },
                }}
              >
                <Popup 
                  maxWidth={400} 
                  maxHeight={400} 
                  className="hover-popup"
                  closeButton={false}
                  autoPan={true}
                  closeOnClick={false}
                  keepInView={true}
                  autoPanPadding={[20, 20]}
                  autoPanPaddingTopLeft={[20, 20]}
                  autoPanPaddingBottomRight={[20, 20]}
                  eventHandlers={{
                    add: (e) => {
                      // Quand le popup est ajouté au DOM, ajouter les gestionnaires d'événements
                      const popupElement = e.target._container;
                      if (popupElement) {
                        const onMouseEnter = () => {
                          if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                            closeTimeoutRef.current = null;
                          }
                        };
                        
                        const onMouseLeave = () => {
                          closeTimeoutRef.current = setTimeout(() => {
                            e.target._source.closePopup();
                            closeTimeoutRef.current = null;
                          }, 300);
                        };
                        
                        popupElement.addEventListener('mouseenter', onMouseEnter);
                        popupElement.addEventListener('mouseleave', onMouseLeave);
                        
                        // Nettoyer les listeners quand le popup est supprimé
                        e.target.once('remove', () => {
                          popupElement.removeEventListener('mouseenter', onMouseEnter);
                          popupElement.removeEventListener('mouseleave', onMouseLeave);
                        });
                      }
                    },
                  }}
                >
                  <div className="max-h-96 overflow-y-auto">
                    {area.tooltip}
                  </div>
                </Popup>
              </Marker>
            ) : null
          )}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default MapComponent;
